import Validation from "../../libraries/validation";
import {EVENT_CONSTANTS} from "../../configs/events";
import {buildValidationToastMessage} from "../../libraries/validation-toast-message";
import {t} from "../../utils/systemTranslations";

const VALIDATION_MIXIN = {
    data: () => ({
        validationErrors: {},
    }),

    methods: {
        /**
         * Get the display label for a control
         * @param {string} controlName
         * @returns {string}
         */
        getControlLabel(controlName) {
            const controls = this.formData?.controls || {};
            for (const [controlId, control] of Object.entries(controls)) {
                const name = control.name || controlId;
                if (name === controlName) {
                    return control.label || control.labelText || this.formatControlName(controlName);
                }
            }
            return this.formatControlName(controlName);
        },

        /**
         * Format control name to be more human-readable
         * @param {string} name
         * @returns {string}
         */
        formatControlName(name) {
            if (!name) return 'Unknown Field';
            return name
                .replace(/([A-Z])/g, ' $1')
                .replace(/_/g, ' ')
                .replace(/-/g, ' ')
                .replace(/\s+/g, ' ')
                .trim()
                .replace(/^\w/, c => c.toUpperCase());
        },

        /**
         * @param {Object} errorBucket {controlName: [array of message]}
         */
        setValidationError(errorBucket) {
            this.$formEvent.$emit(EVENT_CONSTANTS.RENDERER.VALIDATION_FAILED, true)
            this.$set(this, 'validationErrors', errorBucket)

            // Build one alert listing every field that blocked the submit, WITH the reason.
            // It used to tell the patient to "complete" the field for EVERY failure —
            // wrong and unhelpful for a format failure, where the field IS filled in and
            // the only thing the patient needs is the rule's own message.
            const fieldNames = Object.keys(errorBucket);
            const entries = fieldNames.map(name => ({
                label: this.getControlLabel(name),
                messages: Array.isArray(errorBucket[name]) ? errorBucket[name] : [errorBucket[name]],
            }));

            const message = buildValidationToastMessage(entries, {
                single: t('checkThisField', this.language),
                plural: t('checkTheseFields', this.language),
            });

            if (this.$toast) {
                this.$toast.error(message, {
                    timeout: 8000,
                    closeOnClick: true,
                    pauseOnHover: true,
                });
            }

            // Scroll to the first error field
            this.$nextTick(() => {
                const firstField = fieldNames[0];
                if (firstField) {
                    const el = document.querySelector(`[data-control-name="${firstField}"]`);
                    if (el) {
                        const rect = el.getBoundingClientRect();
                        const offset = window.pageYOffset + rect.top - 120;
                        window.scrollTo({ top: offset, behavior: 'smooth' });
                    }
                }
            });
        },

        /**
         * Build a set of control IDs whose parent section is hidden
         */
        getHiddenSectionControlIds() {
            const hidden = new Set()
            const sections = this.formData && this.formData.sections ? this.formData.sections : {}
            const visibleMap = this.visibleMap || {}

            for (const [sectionId, section] of Object.entries(sections)) {
                const sectionKey = section.uniqueId || sectionId
                // If section is explicitly hidden in visibleMap, mark all its controls
                if (Object.prototype.hasOwnProperty.call(visibleMap, sectionKey) && !visibleMap[sectionKey]) {
                    if (Array.isArray(section.controls)) {
                        section.controls.forEach(id => hidden.add(id))
                    }
                    // Also check controls inside rows belonging to this section
                    if (Array.isArray(section.rows)) {
                        const rows = this.formData.rows || {}
                        section.rows.forEach(rowId => {
                            const row = rows[rowId]
                            if (row && Array.isArray(row.controls)) {
                                row.controls.forEach(id => hidden.add(id))
                            }
                        })
                    }
                }
            }
            return hidden
        },

        /**
         * The controls a submit validates: every control that is visible right now. Skips a control
         * hidden by its own visibility rule AND every control inside a hidden section — the
         * "hidden by section" rule behind the 2026-07-06 incident lives HERE, once.
         * runValidation() (final submit) and FormRenderer's per-part check both read it.
         *
         * @returns {Object} controlId -> control
         */
        getControlsForValidation() {
            const allControls = this.formData && this.formData.controls ? this.formData.controls : {}
            const visibleMap = this.visibleMap || {}
            const hiddenBySection = this.getHiddenSectionControlIds()
            const controlsForValidation = {}

            Object.keys(allControls).forEach(controlId => {
                const control = allControls[controlId]
                const key = control.name && control.name !== '' ? control.name : control.uniqueId

                // Skip if control itself is hidden
                if (Object.prototype.hasOwnProperty.call(visibleMap, key) && !visibleMap[key]) {
                    return
                }
                // Skip if control's parent section is hidden
                if (hiddenBySection.has(controlId)) {
                    return
                }
                controlsForValidation[controlId] = control
            })

            return controlsForValidation
        },

        /**
         * Run the validation process
         */
        async runValidation() {
            // always clear validation before run...
            this.$set(this, 'validationErrors', {})

            // Rebuild validation with currently visible controls (respects both
            // control-level and section-level visibility rules at submit time)
            const controlsForValidation = this.getControlsForValidation()

            this.$form.Validation = new Validation(
                this.valueContainer,
                controlsForValidation,
                this.$form.validationClosures || {},
                this.language,
            )

            // run the validation
            const result = this.$form.Validation.run()

            // field-error handling
            if (result.errors()) {
                return this.setValidationError(result.errorBuckets)
            }

            // if we turned on the server-side validation
            if (this.isEnableServerSideValidation) {
                const isServersideValidationOk = await this.requestForServerSideValidation()
                if (!isServersideValidationOk) {
                    return;
                }
            }

            // ok emit to all listener if they want to know the validation is ok or not
            this.$formEvent.$emit(EVENT_CONSTANTS.RENDERER.VALIDATION_OK, true)
        },

        /**
         * Run the server-side validation
         *
         * @returns {Promise<boolean>}
         */
        async requestForServerSideValidation() {
            const validationResult = await fetch(this.serverSideValidationEndpoint, {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                    'Accept': 'application/json'
                },
                body: this.valueContainer
            })

            // oke validation success
            if (validationResult.ok) {
                return true;
            }

            // I consider 422 is failed the validation
            if (validationResult.status === 422) {
                // the body should contains error messages
                const errorsBag = await validationResult.json()
                this.setValidationError(errorsBag)

                return false
            }
        },
    },

    /**
     * Dependencies Injection into the Form-Renderer.
     */
    created() {
        // create validation instance. Only include currently visible controls to validate.
        try {
            const controlsForValidation = {}
            const allControls = this.formData && this.formData.controls ? this.formData.controls : {}
            const visibleMap = this.visibleMap || {}

            Object.keys(allControls).forEach(controlId => {
                const control = allControls[controlId]
                const key = control.name && control.name !== '' ? control.name : control.uniqueId
                if (Object.prototype.hasOwnProperty.call(visibleMap, key) && !visibleMap[key]) {
                    return // skip hidden control
                }
                controlsForValidation[controlId] = control
            })

            this.$form.Validation = new Validation(
                this.valueContainer,
                controlsForValidation,
                this.$form.validationClosures || {},
                this.language,
            )
        } catch (e) {
            // fallback to full controls if anything goes wrong
            this.$form.Validation = new Validation(
                this.valueContainer,
                this.formData.controls,
                this.$form.validationClosures || {},
                this.language,
            )
        }

        // listen to validation invoke
        this.$formEvent.$on(EVENT_CONSTANTS.RENDERER.RUN_VALIDATION, this.runValidation);
    },

    // Pair the $on above with $off — without this, packet flow stacks stale
    // validation listeners and an already-completed prior form's mixin will
    // emit VALIDATION_OK alongside the live form's VALIDATION_FAILED.
    beforeDestroy() {
        this.$formEvent.$off(EVENT_CONSTANTS.RENDERER.RUN_VALIDATION, this.runValidation);
    },

    computed: {
        /**
         * to check if enable server side validation
         *
         * @returns {Boolean}
         */
        isEnableServerSideValidation() {
            if (!this.formConfiguration.formConfig || !this.formConfiguration.formConfig.enableServerSideValidation) {
                return false;
            }

            return this.formConfiguration.formConfig.enableServerSideValidation;
        },

        /**
         * Quick access to the endpoint
         *
         * @returns {any}
         */
        serverSideValidationEndpoint() {
            return this.formConfiguration.formConfig.serverSideValidationEndpoint
        }
    },
}

export {VALIDATION_MIXIN}
